import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiService } from 'api/services/ApiService';
import { AttioQueryKeys, CREATED_AT_KEY, DESC, NOTES_KEY, STATUS_KEY, VALUATION_DATE_KEY, VALUATION_FEE_KEY } from 'common/constants/analyst-dashboards';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { AttioSortDirection, SortCriteria } from 'dashboard409a/common/constants/analyst-dashboard';
import { EngagementManagerOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import { ValuationStatus } from 'dashboard409a/pages/Workboard/common/types';
import { AttioTeamService } from 'dashboard409a/services/attio/AttioTeamService';
import { getAttioUserRecordId } from 'dashboard409a/services/attio-common';
import { getAllAttioValuationsWithBulkData } from 'dashboard409a/services/attio-dashboard';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { Note } from 'dashboard409a/services/models.types';
import { compareValuesForSort } from 'dashboard409a/utilities';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import { getObjectValue } from 'utilities';
import { UseValuationsWithBulkDataProps, UseValuationsWithBulkDataReturn } from './useValuationsWithBulkData.types';


// Constants
const DEFAULT_ORDER_BY = VALUATION_DATE_KEY;
const DEFAULT_ORDER = DESC;
const USER_RECORD_CACHE_TIME = 60 * 60 * 1000; // 60 minutes
const VALUATIONS_SORT_KEY = 'valuationsSort';
// Generate sorted status options from enum values in workflow order
const SORTED_STATUS_OPTIONS = Object.values(ValuationStatus).map((status, index) => ({
  value: status,
  weight: index + 1
}))

const getStatusWeight = (status: string): number => {
  const statusOption = SORTED_STATUS_OPTIONS.find(option => option.value === status);
  return statusOption?.weight || 0;
};

// Column type sets for dynamic sorting — keys match record field names
const DATE_COLUMNS = new Set([
  VALUATION_DATE_KEY, CREATED_AT_KEY,
  'deal_due', 'pay_1_date', 'pay_2_date', 'pay_3_date',
  'refund_date', 'adjustment_date', 'bad_debt_date',
]);

const CURRENCY_COLUMNS = new Set([
  VALUATION_FEE_KEY,
  'net_value', 'split', 'payment_1', 'payment_2', 'payment_3',
  'refund', 'adjustment', 'bad_debt', 'renewal_rate',
]);

// Local sorting function - moved outside component to avoid recreation
const getValue = (record: SimplifiedAttioValuation, sortBy: string): string | number | null => {
  if (sortBy === STATUS_KEY) {
    return getStatusWeight(record.status || '');
  }
  if (sortBy === NOTES_KEY) {
    return record.note?.content || '';
  }

  if (DATE_COLUMNS.has(sortBy)) {
    const dateStr = record[sortBy as keyof SimplifiedAttioValuation] as string | null;
    if (!dateStr) {
      return null;
    }
    const ms = new Date(dateStr).getTime();
    return Number.isNaN(ms) ? null : ms;
  }

  if (CURRENCY_COLUMNS.has(sortBy)) {
    const raw = record[sortBy as keyof SimplifiedAttioValuation] as string | null;
    if (!raw) {
      return null;
    }
    const num = Number(raw.replaceAll(/[^0-9.-]/g, ''));
    return Number.isNaN(num) ? null : num;
  }

  // Default: treat as string field (company_name, engagement_manager, deal, etc.)
  const value = record[sortBy as keyof SimplifiedAttioValuation];
  return (typeof value === 'string' ? value : '') || '';
};

const sortValuationsLocally = (valuations: SimplifiedAttioValuation[], sortBy: string, sortOrder: AttioSortDirection) => {
  localStorage.setItem(VALUATIONS_SORT_KEY, JSON.stringify({ sortBy, sortOrder }));

  return [...valuations].sort((a, b) => {
    const aValue = getValue(a, sortBy);
    const bValue = getValue(b, sortBy);
    return compareValuesForSort(aValue, bValue, sortOrder);
  });
};

// Multi-criteria sorting function
const sortValuationsWithMultipleCriteria = (valuations: SimplifiedAttioValuation[], criteria: SortCriteria[]): SimplifiedAttioValuation[] => {
  if (criteria.length === 0) {
    return [...valuations];
  }

  // Save to localStorage
  localStorage.setItem(VALUATIONS_SORT_KEY, JSON.stringify({ sortCriteria: criteria }));

  return [...valuations].sort((a, b) =>
    criteria.reduce((compareResult, { property, direction }) => {
      if (compareResult !== 0) {
        return compareResult;
      }
      const aValue = getValue(a, property);
      const bValue = getValue(b, property);
      return compareValuesForSort(aValue, bValue, direction);
    }, 0)
  );
};

const getSortedValuations = (
  valuations: SimplifiedAttioValuation[],
  sortCriteria: SortCriteria[] | undefined,
  orderBy: string,
  order: AttioSortDirection
): SimplifiedAttioValuation[] => {
  if (sortCriteria && sortCriteria.length > 0) {
    return sortValuationsWithMultipleCriteria(valuations, sortCriteria);
  }
  const localSort = localStorage.getItem(VALUATIONS_SORT_KEY);
  if (localSort) {
    try {
      const parsed = JSON.parse(localSort);
      if (parsed.sortCriteria?.length > 0) {
        return sortValuationsWithMultipleCriteria(valuations, parsed.sortCriteria);
      }
      if (parsed.sortBy) {
        return sortValuationsLocally(valuations, parsed.sortBy, parsed.sortOrder || DEFAULT_ORDER);
      }
    } catch {
      // fall through to default
    }
  }
  return sortValuationsLocally(valuations, orderBy, order);
};

const buildDisplayedValuations = (
  sortedValuations: SimplifiedAttioValuation[],
  prevDisplayedValuations: SimplifiedAttioValuation[]
): SimplifiedAttioValuation[] => {
  const existingDataMap = new Map(
    prevDisplayedValuations.map(record => [record.record_id, record])
  );

  return sortedValuations.map(record => {
    const existingData = existingDataMap.get(record.record_id);
    if (existingData) {
      return {
        ...record,
        note: existingData.note ?? record.note ?? null,
        is_loading_additional_data: existingData.is_loading_additional_data ?? false,
      };
    }
    return { ...record, is_loading_additional_data: false };
  });
};

export const useValuationsWithBulkData = ({
  filters,
  orderBy = DEFAULT_ORDER_BY,
  order = DEFAULT_ORDER,
  sortCriteria,
  searchParams = { searchParam: '', searchAll: false }
}: UseValuationsWithBulkDataProps): UseValuationsWithBulkDataReturn => {
  const queryClient = useQueryClient();
  
  // State for data loading
  const [displayedValuations, setDisplayedValuations] = useState<SimplifiedAttioValuation[]>([]);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useLocalStorage<string>('selectedUserEmail', '');
  const [totalCount, setTotalCount] = useState(0);
  const [allValuations, setAllValuations] = useState<SimplifiedAttioValuation[]>([]);
  const [statusJustChanged, setStatusJustChanged] = useState(false);
  // Get user email from store
  const [{ user }] = useStore() as unknown as UseStoreValues;
  
  const { email: userEmail = '', is_staff: isStaff  } = getObjectValue(user);

  // Get user record ID
  // For superusers: use selectedUserEmail if provided, otherwise fall back to userEmail
  // For non-superusers: always use userEmail
  // Never use empty string - always fall back to userEmail if selectedUserEmail is empty
  const effectiveUserEmail = selectedUserEmail || userEmail;
  const { data: userRecordId, isLoading: isLoadingUserRecord, isFetched: isUserRecordFetched } = useQuery({
    queryKey: [AttioQueryKeys.USER_RECORD_ID, effectiveUserEmail],
    queryFn: () => getAttioUserRecordId(effectiveUserEmail),
    staleTime: USER_RECORD_CACHE_TIME,
    retry: false,
    enabled: !!effectiveUserEmail && effectiveUserEmail.trim() !== '', // Only run if userEmail exists and is not empty
  });

  /// Get users for select list
  const { data: staffUsers = [], isLoading: isLoadingStaffUsers } = useQuery({
    queryKey: ['users-list', 'staff'],
    queryFn: () => ApiService.apiUsersStaffList(),
    enabled: isStaff,
    staleTime: USER_RECORD_CACHE_TIME,
    retry: true,
  });

  // Reset all state when the effective user changes (e.g., when selectedUserEmail is cleared)
  useEffect(() => {
    setDisplayedValuations([]);
    setAllValuations([]);
    setIsInitialLoadComplete(!effectiveUserEmail); // If no email, nothing will load — mark complete immediately
    setTotalCount(0);
  }, [effectiveUserEmail]);

  // If user record query completed but found no record, there's nothing to load
  useEffect(() => {
    if (isUserRecordFetched && !userRecordId) {
      setIsInitialLoadComplete(true);
    }
  }, [isUserRecordFetched, userRecordId]);

  // Load all valuations with bulk data in one call
  const { data: allValuationsData, isLoading: isLoadingAllValuations } = useQuery({
    queryKey: ['valuations-all', userRecordId, filters, searchParams],
    queryFn: () => getAllAttioValuationsWithBulkData({
      userRecordId: userRecordId!,
      filters,
      searchParams: searchParams ?? { searchParam: '', searchAll: false },
    }),
    enabled: !!userRecordId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 10 * 60 * 1000, // 10 minutes cache
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
  });

  // Load initial data when all valuations are loaded (now includes bulk data)
  useEffect(() => {
    if (allValuationsData) {
      if (allValuationsData.data && allValuationsData.data.length > 0) {
        // Store all valuations (already enriched with bulk data from backend)
        setAllValuations(allValuationsData.data);
        setTotalCount(allValuationsData.totalCount || allValuationsData.data.length);
        
        if (displayedValuations.length === 0) {
          // Data is already enriched with all bulk data including notes
          const enrichedRecords = allValuationsData.data.map((record: SimplifiedAttioValuation) => ({
            ...record,
            is_loading_additional_data: false, // All bulk data (including notes) is already loaded
          }));
          setDisplayedValuations(enrichedRecords);
          setIsInitialLoadComplete(true);
        }
      } else {
        // No data case - set empty arrays and mark as complete
        setAllValuations([]);
        setDisplayedValuations([]);
        setTotalCount(0);
        setIsInitialLoadComplete(true);
      }
    }
  }, [allValuationsData, displayedValuations.length]);

  // Apply local sorting when data or sorting parameters change
  useEffect(() => {
    if (allValuations.length > 0) {
      // Determine if we should apply delay: only when sorting by status AND status was just changed
      const primarySortProperty = sortCriteria && sortCriteria.length > 0 ? sortCriteria[0].property : orderBy;
      const isSortingByStatus = primarySortProperty === STATUS_KEY;
      const shouldDelay = isSortingByStatus && statusJustChanged;
      const delay = shouldDelay ? 5000 : 0;

      const performSort = () => {
        const sortedValuations = getSortedValuations(allValuations, sortCriteria, orderBy, order);
        setDisplayedValuations(prev => buildDisplayedValuations(sortedValuations, prev));
        if (shouldDelay) {
          setStatusJustChanged(false);
        }
      };
      
      const timeoutId = setTimeout(performSort, delay);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
    return undefined;
  }, [allValuations, orderBy, order, queryClient, statusJustChanged, sortCriteria]);

  // Reset status change flag when sorting changes to something other than status
  useEffect(() => {
    if (orderBy !== STATUS_KEY && statusJustChanged) {
      setStatusJustChanged(false);
    }
  }, [orderBy, statusJustChanged]);



  // Functions to update individual valuations with cache invalidation
  const updateValuation = useCallback((updatedValuation: SimplifiedAttioValuation) => {
    // Update displayed valuations optimistically
    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === updatedValuation.record_id 
          ? { ...valuation, ...updatedValuation }
          : valuation
      )
    );

    // Invalidate and refetch the main query to ensure consistency
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.VALUATIONS_ALL, userRecordId, filters, searchParams]
    });
  }, [queryClient, userRecordId, filters, searchParams]);

  const updateValuationStatus = useCallback((recordId: string, newStatus: string) => {
    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { ...valuation, status: newStatus }
          : valuation
      )
    );

    setAllValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { ...valuation, status: newStatus }
          : valuation
      )
    );
    
    // Mark that status was just changed to trigger delayed sorting if sorting by status
    setStatusJustChanged(true);
  }, []);

  const updateValuationNotes = useCallback((recordId: string, newNotes: string, noteId?: number) => {
    // Create updated note object
    const updatedNote: Note = {
      id: noteId ?? 0,
      content: newNotes,
      author_id: 0,
      author_email: '',
      author_name: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Note: Notes are now included in the main valuations query, so we invalidate that instead

    // Update displayed valuations optimistically
    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { 
            ...valuation, 
            note: updatedNote
          }
          : valuation
      )
    );

    // Invalidate cache to ensure data consistency
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.VALUATIONS_ALL, userRecordId, filters, searchParams]
    });

    // Invalidate additional data cache for this specific record
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.ADDITIONAL_DATA],
      predicate: query => {
        const {queryKey} = query;
        if (Array.isArray(queryKey) && queryKey[0] === AttioQueryKeys.ADDITIONAL_DATA) {
          const recordIds = queryKey[1]?.toString().split(',') || [];
          return recordIds.includes(recordId);
        }
        return false;
      }
    });
  }, [queryClient, userRecordId, filters, searchParams]);

  const updateValuationEngagementManager = useCallback(async (
    recordId: string, 
    engagementManagerOption: EngagementManagerOption
  ) => {
    // Use the engagement manager service to load individual data (for updates only)
    const teamService = new AttioTeamService();
    const managerData = await teamService.getTeamMember(engagementManagerOption.targetRecordId);

    const engagementManagerData = {
      name: managerData.name,
      imageSrc: managerData.imageSrc,
    };
    
    queryClient.setQueryData(
      [AttioQueryKeys.ENGAGEMENT_MANAGER, engagementManagerOption.targetRecordId],
      engagementManagerData
    );


    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { 
            ...valuation, 
            engagement_manager: engagementManagerOption.name,
            engagement_manager_image_src: engagementManagerOption.imageSrc,
            engagement_manager_id: engagementManagerOption.targetRecordId,
          }
          : valuation
      )
    );
  }, [queryClient]);

  return {
    // Data
    rows: displayedValuations,
    totalCount,
    staffUsers,
    isLoadingStaffUsers,
    setSelectedUserEmail,
    selectedUserEmail,

    // Loading states
    isLoading: !!effectiveUserEmail && (isLoadingUserRecord || isLoadingAllValuations || !isInitialLoadComplete),
    isError: false,
    error: null,
    
    // Update functions
    updateValuation,
    updateValuationStatus,
    updateValuationNotes,
    updateValuationEngagementManager,
  };
};
